import React, { createContext, useState, useContext, useEffect } from 'react';
import { createTheme } from '@mui/material/styles';

const ThemeContext = createContext();

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: "#0066B2",
    },
    secondary: {
      main: "#009E60",
    },
    background: {
      default: "#f8fafc",
      paper: "#ffffff",
    },
    text: {
      primary: "#0f172a",
      secondary: "#64748b",
    },
    appBar: {
      backgroundColor: "#ffffff",
      textColor: "#0f172a",
      iconColor: "#0f172a",
    },
    sidebar: {
      backgroundColor: "#0066B2",
      textColor: "#ffffff",
      iconColor: "#ffffff",
    }
  },
  typography: {
    fontFamily: ["Manrope", "sans-serif"].join(","),
    fontSize: 14,
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: "#0066B2",
    },
    secondary: {
      main: "#009E60",
    },
    background: {
      default: "#0f172a",
      paper: "#1e293b",
    },
    text: {
      primary: "#f1f5f9",
      secondary: "#cbd5e1",
    },
    appBar: {
      backgroundColor: "#1e293b",
      textColor: "#f1f5f9",
      iconColor: "#f1f5f9",
    },
    sidebar: {
      backgroundColor: "#0f172a",
      textColor: "#f1f5f9",
      iconColor: "#f1f5f9",
    }
  },
  typography: {
    fontFamily: ["Manrope", "sans-serif"].join(","),
    fontSize: 14,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#0f172a",
          color: "#f1f5f9",
        },
        html: {
          backgroundColor: "#0f172a",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#1e293b",
          color: "#f1f5f9",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#1e293b",
          color: "#f1f5f9",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            color: '#f1f5f9',
            backgroundColor: '#0f172a',
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          backgroundColor: "#1e293b",
          color: "#f1f5f9",
          '& .MuiDataGrid-cell': {
            color: "#f1f5f9",
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: "#0f172a",
            color: "#f1f5f9",
          },
          '& .MuiDataGrid-row': {
            backgroundColor: "#1e293b",
            '&:hover': {
              backgroundColor: "#334155",
            },
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.dark-text-black': {
            color: "#f1f5f9",
          },
          '&.dark-text-gray': {
            color: "#f1f5f9",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '&.dark-text-button': {
            color: "#f1f5f9",
          },
        },
      },
    },
  },
});

export const ThemeProviderCustom = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Get dark mode preference from localStorage
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    // Save preference to localStorage
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    // Apply data-mode attribute to document for CSS dark mode handling
    document.documentElement.setAttribute('data-mode', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Apply CSS variables on first mount
  useEffect(() => {
    if (!document.getElementById('dark-mode-styles')) {
      const style = document.createElement('style');
      style.id = 'dark-mode-styles';
      style.textContent = `
        [data-mode="dark"] {
          --text-black: #f1f5f9;
          --text-gray: #f1f5f9;
          --text-gray-secondary: #cbd5e1;
        }
        
        [data-mode="light"] {
          --text-black: #0f172a;
          --text-gray: #64748b;
          --text-gray-secondary: #6b7280;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  const theme = isDarkMode ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode, theme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within ThemeProviderCustom');
  }
  return context;
};
